/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from "react";
import Select from "react-select";
import { Info } from "lucide-react";
import { useMenuPlacement } from "../hooks";
import { defaultSelectCustomStyles, handleSelectMenuOpen } from "../utils";
import ToolTip from "../../../ToolTip/ToolTip";
import {
  CustomMenuList,
  CustomMultiValue,
  CustomOption,
} from "./Select/components";
import { MultiSelectProps, SelectItems } from "../interfaces";

export const MultiSelect: React.FC<MultiSelectProps> = (props) => {
  const {
    allButtonLabel = "Todos",
    disabled,
    error,
    isRequired,
    items,
    itemsSelectedLabel = "Items",
    label,
    menuListHeight = 190,
    onChange,
    placeholder = "Selecciona opciones",
    tooltipText,
    value,
  } = props;
  const selectRef: React.MutableRefObject<null> = useRef(null);
  const [inputValue, setInputValue] = useState<string>("");
  const { containerRef, menuPlacement, setIsMenuOpen } = useMenuPlacement({
    isMultiSelect: true,
    menuListHeight,
  });

  const customStyles: typeof defaultSelectCustomStyles = {
    ...defaultSelectCustomStyles,
    control: (styles: object, state: any) => ({
      ...defaultSelectCustomStyles.control(styles, state),
      cursor: "pointer",
    }),
  };

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-normal text-gray-500">
            {label}{" "}
            {isRequired && (
              <span className="text-xs font-normal text-gray-500">*</span>
            )}
          </label>
          {tooltipText && (
            <ToolTip text={tooltipText}>
              <Info className="h-3 w-3 shrink-0 text-gray-500" />
            </ToolTip>
          )}
        </div>
      )}
      <div ref={containerRef}>
        <Select
          ref={selectRef}
          onMenuOpen={() => {
            setIsMenuOpen(true);
            handleSelectMenuOpen();
          }}
          onMenuClose={() => {
            setIsMenuOpen(false);
            setInputValue("");
          }}
          classNamePrefix="custom-select"
          menuShouldScrollIntoView={false}
          isMulti={true}
          isClearable={false}
          backspaceRemovesValue={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isDisabled={disabled}
          blurInputOnSelect={false}
          placeholder={placeholder}
          noOptionsMessage={() => "Sin resultados"}
          options={items.map((item: SelectItems) => ({
            label: item.name,
            value: item.path,
            isDisabled: item.isDisabled || false,
          }))}
          defaultValue={value}
          value={value}
          inputValue={inputValue}
          onInputChange={(newValue, actionMeta) => {
            if (actionMeta.action === "input-change") setInputValue(newValue);
          }}
          onChange={onChange}
          components={{
            MultiValue: CustomMultiValue,
            MenuList: CustomMenuList,
            Option: CustomOption,
          }}
          styles={customStyles}
          minMenuHeight={menuListHeight}
          maxMenuHeight={menuListHeight}
          menuPortalTarget={document.body}
          menuPlacement={menuPlacement}
          theme={(theme) => ({
            ...theme,
            borderRadius: 6,
            colors: {
              ...theme.colors,
              primary25: "rgb(var(--select-option-active))",
              primary: "rgb(var(--line-gray-200))",
            },
          })}
          {...{
            allButtonLabel,
            itemsSelectedLabel,
          }}
        />
      </div>
      {error && <label className="text-xs text-red-500">{error}</label>}
    </div>
  );
};
